import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  TextField,
  Button,
  Box,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import SourceDataGrid from "../../Components/SourceDataGrid/SourceDataGrid";
import SearchIcon from "@mui/icons-material/Search";
import { fetchIndividualRecords, resetAPIState } from "./individualSearchSlice";
import { useDispatch, useSelector } from "react-redux";
import "./IndividualSearchPage.css";
import { updateAuthorized } from "../../../DataExtractionTool/Authentication/authenticationSlice";
import { useNavigate } from "react-router";

const IndividualSearchPage = () => {
  const { register, handleSubmit } = useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isAuthorised } = useSelector((state) => state.authentication);
  const { isSessionValid } = useSelector((state) => state.individualSearch);

  useEffect(() => {
    if (!isAuthorised) {
      navigate("/");
    }
  }, [isAuthorised]);

  if (!isSessionValid) {
    dispatch(updateAuthorized(false));
  }

  const { records, status, error, queueStatus } = useSelector(
    (state) => state.individualSearch
  );

  // Load initial data (last month's records)
  useEffect(() => {
    dispatch(fetchIndividualRecords({}));
  }, [dispatch]);

  const onSubmit = (data) => {
    console.log("Search submitted with:", data);
    dispatch(fetchIndividualRecords(data));
  };

  useEffect(() => {
    if (status === "succeeded" && records.length > 0) {
      console.log("Fetched Records:", records);
    }
  }, [records, status]);

  useEffect(() => {
    if (queueStatus === "succeeded") {
      alert("Record successfully queued for validation!");
      dispatch(resetAPIState());
      dispatch(fetchIndividualRecords({}));
    }
  }, [queueStatus]);

  return (
    <div className="individual-search-page">
      <Typography
        variant="h5"
        className="main-title"
        sx={{
          textAlign: "center",
          fontWeight: "bold",
          color: "#2E3B55",
          marginBottom: "1.5rem",
          fontFamily: "Montserrat, sans-serif",
        }}
      >
        Individual Search
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        className="search-container"
      >
        <div className="search-elements">
          <Typography
            variant="subtitle1"
            className="instruction-text"
            sx={{ fontWeight: "bold" }}
          >
            Search for a record to validate:
          </Typography>
          <TextField
            label="OneKey ID"
            {...register("oneKeyId")}
            variant="outlined"
            size="small"
            className="search-field"
          />
          <TextField
            label="AHPRA ID"
            {...register("ahpraId")}
            variant="outlined"
            size="small"
            className="search-field"
          />
          <TextField
            label="First Name"
            {...register("firstName")}
            variant="outlined"
            size="small"
            className="search-field"
          />
          <TextField
            label="Last Name"
            {...register("lastName")}
            variant="outlined"
            size="small"
            className="search-field"
          />
          <Tooltip title="Search">
            <Button
              type="submit"
              variant="contained"
              size="medium"
              className="search-button"
              sx={{
                backgroundColor: "#2E3B55",
                "&:hover": {
                  backgroundColor: "#1d283c",
                },
              }}
            >
              <SearchIcon />
            </Button>
          </Tooltip>
        </div>
      </Box>

      {/* Display Spinner when Loading */}
      {status === "loading" ? (
        <Box
          className="spinner-container"
          sx={{ textAlign: "center", marginTop: "2rem" }}
        >
          <CircularProgress sx={{ color: "#2E3B55" }} />
          <Typography
            variant="body1"
            sx={{ marginTop: "1rem", color: "#2E3B55" }}
          >
            Loading Records...
          </Typography>
        </Box>
      ) : (
        <SourceDataGrid rows={records} />
      )}

      {status === "failed" && (
        <Typography
          color="error"
          variant="body1"
          sx={{ textAlign: "center", marginTop: "1rem" }}
        >
          {error || "Error fetching records. Please try again."}
        </Typography>
      )}
    </div>
  );
};

export default IndividualSearchPage;
